import { computed, type Ref } from "vue";

type MaskPart =
  | { kind: "token"; token: "D" | "M" | "Y" | "H" | "m" | "s"; len: number; maxLen: number }
  | { kind: "literal"; text: string };

const TOKEN_SET = new Set(["D", "M", "Y", "H", "m", "s"]);

export function useDateMask(inputFormat: Ref<string>) {
  function parseFormatToMask(fmt: string): MaskPart[] {
    if (!fmt) return [];
    const parts: MaskPart[] = [];
    let i = 0;
    while (i < fmt.length) {
      const ch = fmt[i]!;
      if (TOKEN_SET.has(ch)) {
        let j = i + 1;
        while (j < fmt.length && fmt[j] === ch) j++;
        const len = j - i;
        const maxLen = ch === "Y" ? Math.max(2, len) : 2;
        parts.push({ kind: "token", token: ch as any, len, maxLen });
        i = j;
      } else {
        let j = i + 1;
        while (j < fmt.length && !TOKEN_SET.has(fmt[j]!)) j++;
        parts.push({ kind: "literal", text: fmt.slice(i, j) });
        i = j;
      }
    }
    return parts;
  }

  const maskParts = computed(() => parseFormatToMask(inputFormat.value));
  const totalTokenDigits = computed(() =>
    maskParts.value.reduce((acc, p) => (p.kind === "token" ? acc + p.maxLen : acc), 0)
  );
  const maskedLength = computed(() =>
    maskParts.value.reduce((acc, p) => acc + (p.kind === "token" ? p.maxLen : p.text.length), 0)
  );

  function applyDynamicMask(raw: string): string {
    const digits = raw.replace(/\D/g, "").slice(0, totalTokenDigits.value);
    let out = "";
    let cursor = 0;

    for (let idx = 0; idx < maskParts.value.length; idx++) {
      const part = maskParts.value[idx]!;
      if (part.kind === "literal") {
        out += part.text;
        continue;
      }
      const slice = digits.slice(cursor, cursor + part.maxLen);
      out += slice;
      cursor += slice.length;

      if (slice.length < part.len) break;
    }
    return out;
  }

  // Deletion over separators
  function isDigit(ch: string | undefined) {
    return !!ch && ch >= "0" && ch <= "9";
  }
  function digitsOnly(s: string) {
    return s.replace(/\D/g, "");
  }
  function removeDigitAt(d: string, index: number) {
    if (index < 0 || index >= d.length) return d;
    return d.slice(0, index) + d.slice(0 + index + 1);
  }
  function caretFromDigitCount(masked: string, digitCount: number) {
    let count = 0;
    for (let i = 0; i < masked.length; i++) {
      if (count === digitCount) {
        let j = i;
        while (j < masked.length && !isDigit(masked[j])) j++;
        return j;
      }
      if (isDigit(masked[i])) count++;
    }
    return masked.length;
  }

  function handleKeydown(
    e: KeyboardEvent,
    input: HTMLInputElement,
    emitValue: (val: string) => void
  ) {
    const key = e.key;
    if (key !== "Backspace" && key !== "Delete") return;
    if (input.selectionStart == null || input.selectionEnd == null) return;
    const start = input.selectionStart;
    const end = input.selectionEnd;
    if (end > start) return;

    const val = input.value;
    const prevChar = val[start - 1];
    const nextChar = val[start];

    if (key === "Backspace" && start > 0 && !isDigit(prevChar)) {
      e.preventDefault();
      const digits = digitsOnly(val);
      const digitsBeforeLiteral = digitsOnly(val.slice(0, start - 1)).length;
      const removeIndex = digitsBeforeLiteral - 1;
      const newDigits = removeDigitAt(digits, removeIndex);
      const masked = applyDynamicMask(newDigits);
      const caretDigitsCount = Math.max(0, digitsBeforeLiteral - 1);
      const newCaret = caretFromDigitCount(masked, caretDigitsCount);
      input.value = masked;
      input.setSelectionRange(newCaret, newCaret);
      emitValue(masked);
      return;
    }

    if (key === "Delete" && !isDigit(nextChar)) {
      e.preventDefault();
      const digits = digitsOnly(val);
      const digitsBeforeLiteral = digitsOnly(val.slice(0, start)).length;
      const removeIndex = digitsBeforeLiteral;
      const newDigits = removeDigitAt(digits, removeIndex);
      const masked = applyDynamicMask(newDigits);
      const caretDigitsCount = digitsBeforeLiteral;
      const newCaret = caretFromDigitCount(masked, caretDigitsCount);
      input.value = masked;
      input.setSelectionRange(newCaret, newCaret);
      emitValue(masked);
      return;
    }
  }

  return {
    maskedLength,
    applyDynamicMask,
    handleKeydown,
  };
}
